<template>
<div>
  <div class="report-container" id="report-container">

    <div class="table-column label-column">
      <!-- Balance Sheet -->

      <div class="table-header">
        <h1 class="header-title">{{ $gettext('Balance Sheet') }}</h1>
      </div>
      <div class="table-cell">
        <h3>{{ $gettext('Assets') }}</h3>
      </div>
      <div class="table-cell">
        {{ $gettext('Balance Bank Account') }}
      </div>
      <div class="table-cell">
        {{ $gettext('Cash & Money equivalents') }}
        <font-awesome-icon id="tooltip-cashandmoney" class="tooltip-icon tooltip-icon--question" icon="fa-regular fa-circle-question" />

        <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-cashandmoney" triggers="hover" container="#report-container">
          <div class="tooltip-title">
              {{ $gettext('Cash & Money') }}
          </div>
          <div class="tooltip-text">
              {{ $gettext('Here you can add any funds that are not recorded in Gekko, such as cash holdings.') }}
          </div>
        </b-tooltip>
      </div>
      <div class="table-cell">
        {{ $gettext('Accounts Receivable') }}
        <font-awesome-icon id="tooltip-receivable" class="tooltip-icon tooltip-icon--question" icon="fa-regular fa-circle-question" />

        <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-receivable" triggers="hover" container="#report-container">
          <div class="tooltip-title">
              {{ $gettext('Accounts Receivable') }}
          </div>
          <div class="tooltip-text">
              {{ $gettext('In this field the gross of invoices  that are not yet paid by your customers in this year is added up.') }}
          </div>
        </b-tooltip>
      </div>
      <div class="table-cell">
        {{ $gettext('Investments') }}
        <GekkoButton 
          @click="changeToggleState('balance_investments')"
          :text="$gettext('Investments per cost type')"
          :icon="toggleArrowIcon(showInvestmentsPerCostType)"
          :icon-right="true"
          class="breakdown-link"
          variant="link"
        />
      </div>
      <div v-if="getLineNamesByType('balance_investments')">
        
        <div v-if="getLineNamesByType('balance_investments').length">
          <div v-for="line_name in getLineNamesByType('balance_investments')" v-bind:key="line_name.key" class="table-cell dropdown-label">
            <span>{{line_name.name}}</span> 
            
            <font-awesome-icon 
              v-if="!isCostTypeBalanceSheet(line_name.costType)"
              id="tooltip-nobalancesheet"
              style="margin-left: 5px;"
              icon="fa-solid fa-triangle-exclamation"
              class="tooltip-icon tooltip-icon--warning"
            />

            <b-tooltip :custom-class="'tooltip-padded'" v-if="!isCostTypeBalanceSheet(line_name.costType)" target="tooltip-nobalancesheet" triggers="hover" container="#report-container">
              <div class="tooltip-title">
                {{ $gettext('No Balance Sheet Cost type') }}
              </div>
              <div class="tooltip-text">
                {{ $gettext('This cost is not classified as balance sheet anymore but it is included in the investments table.') }}
              </div>
            </b-tooltip>
          </div>
        </div>
        <div v-if="!getLineNamesByType('balance_investments').length" class="empty-text-container dropdown-label">
          <span class="empty-array-text" v-translate>No investments found</span>
        </div>

      </div>
      <div class="table-cell cell-spacer-double" />
      <div class="table-cell cell-border-top top-cell-element">
        <h3>{{ $gettext('Liabilities') }}</h3>
      </div>
      <div class="table-cell">
        {{ $gettext('Unpaid VAT') }}
        <font-awesome-icon id="tooltip-unpaidvat" class="tooltip-icon tooltip-icon--question" icon="fa-regular fa-circle-question" />
        <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-unpaidvat" triggers="hover" container="#report-container">
          <div class="tooltip-title">
              {{ $gettext('Unpaid VAT') }}
          </div>
          <div class="tooltip-text">
              {{ $gettext('This always reflects the amount of VAT you have to pay in Q4 of this year. This will be set to 0 as long as you did not calculate the Q4 VAT report.') }}
          </div>
        </b-tooltip>
      </div>
      <div class="table-cell">
        {{ $gettext('Accounts Payable') }}
        <font-awesome-icon id="tooltip-accountspayable" class="tooltip-icon tooltip-icon--question" icon="fa-regular fa-circle-question" />
        <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-accountspayable" triggers="hover" container="#report-container">
          <div class="tooltip-title">
              {{ $gettext('Accounts Payable') }}
          </div>
          <div class="tooltip-text">
              {{ $gettext('In this field the gross of expenses that have not yet been paid by your company is added up.') }}
          </div>
        </b-tooltip>
      </div>
      <div class="table-cell">
        {{ $gettext('Other Debts') }}
        <font-awesome-icon id="tooltip-otherdebts" class="tooltip-icon tooltip-icon--question" icon="fa-regular fa-circle-question" />
        <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-otherdebts" triggers="hover" container="#report-container">
          <div class="tooltip-title">
              {{ $gettext('Other Debts') }}
          </div>
          <div class="tooltip-text">
              {{ $gettext('Here you can add any outstanding debts or liabilities that will lower you Equity.') }}
          </div>
        </b-tooltip>
      </div>
      <div class="table-cell cell-spacer-single" />
      <div class="table-cell cell-border-top top-cell-element">
        <h3>{{ $gettext('Equity') }}</h3>
      </div>

      <!-- Profit and Loss -->

      <div class="table-cell cell-spacer-double" />

      <div class="table-cell table-header">
        <h1 class="header-title">{{ $gettext('Profit & Loss') }}</h1>
      </div>
      <div class="table-cell">
        <h3>{{ $gettext('Total Revenue') }}</h3>
      </div>
      <div class="table-cell">
        {{ $gettext('Unpaid Invoices') }}
      </div>
      <div class="table-cell">
        {{ $gettext('Paid Invoices') }}
      </div>
      <div class="table-cell">
        {{ $gettext('Corrections') }}
      </div>
      <div class="table-cell cell-spacer-single" />
      <div class="table-cell cell-border-top top-cell-element">
        <h3>{{ $gettext('Total Costs') }}</h3>
      </div>
      <div class="table-cell">
        {{ $gettext('Expenses') }}
        <GekkoButton 
          @click="changeToggleState('profit_expenses')"
          :text="$gettext('Expenses per cost type')"
          :icon="toggleArrowIcon(showExpensesPerCostType)"
          :icon-right="true"
          class="breakdown-link"
          variant="link"
        />
      </div>
      <div v-if="getLineNamesByType('profit_expenses')">

        <div v-if="getLineNamesByType('profit_expenses').length">
          <div v-for="line_name in getLineNamesByType('profit_expenses')" v-bind:key="line_name.key" class="table-cell dropdown-label">
            {{line_name.name}}
          </div>
        </div>
        <div v-if="!getLineNamesByType('profit_expenses').length" class="table-cell dropdown-label">
          <span class="empty-array-text" v-translate>No expenses found</span>
        </div>

      </div>
      <div class="table-cell cell-spacer-single" />

      <div class="table-cell">
        {{ $gettext('Write Offs') }}
        <GekkoButton 
          @click="changeToggleState('profit_writeoffs')"
          :text="$gettext('Write offs per cost type')"
          :icon="toggleArrowIcon(showWriteOffsPerCostType)"
          :icon-right="true"
          class="breakdown-link"
          variant="link"
        />
      </div>
      <div v-if="getLineNamesByType('profit_writeoffs')">

        <div v-if="getLineNamesByType('profit_writeoffs').length">
          <div v-for="line_name in getLineNamesByType('profit_writeoffs')" v-bind:key="line_name.key" class="table-cell dropdown-label">
            <span>{{line_name.name}}</span> 
              
              <font-awesome-icon 
                v-if="!isCostTypeBalanceSheet(line_name.costType)"
                id="tooltip-nobalancesheet-writeoff"
                style="margin-left: 5px;"
                icon="fa-solid fa-triangle-exclamation"
                class="tooltip-icon tooltip-icon--warning"
              />  

              <b-tooltip :custom-class="'tooltip-padded'" v-if="!isCostTypeBalanceSheet(line_name.costType)" target="tooltip-nobalancesheet-writeoff" triggers="hover" container="#report-container">
                <div class="tooltip-title">
                  {{ $gettext('No Balance Sheet Cost type') }}
                </div>
                <div class="tooltip-text">
                  {{ $gettext('This cost is not classified as balance sheet anymore but it is included in the investments table.') }}
                </div>
              </b-tooltip>
          </div>
        </div>
        <div class="empty-text-container dropdown-label" v-if="!getLineNamesByType('profit_writeoffs').length">
          <span class="empty-array-text" v-translate>No write offs found</span>
        </div>

      </div>
      <div class="table-cell cell-spacer-single" />

      <div class="table-cell cell-border-top top-cell-element">
        <h3>{{ $gettext('Profit') }}</h3>
      </div>
      <div class="table-cell cell-spacer-double" />

      <!-- Withdrawals & deposits -->

      <div class="table-cell table-header">
        <h1 class="header-title">{{ $gettext('Withdrawals & Deposits') }}</h1>
      </div>
      <div class="table-cell">
        <h3>
          {{ $gettext('Total W&D') }}
          
          <font-awesome-icon id="tooltip-withdrawals" class="tooltip-icon tooltip-icon--question" icon="fa-regular fa-circle-question" />

          <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-withdrawals" triggers="click" container="#report-container">
            <div class="tooltip-title">
                {{ $gettext('Withdrawals & Deposits') }}
            </div>
            <div class="tooltip-text">
                {{ $gettext('If your equity at the end of a year does not match with the profit you made in a year, Gekko considers this a withdrawal or deposit into the equity of your company. So this field is a simple calculation: Equity this year - Equity Previous year - Profit. Negative amounts indicate withdrawals and positive amounts indicate deposits.') }}
            </div>
          </b-tooltip>
        </h3>
      </div>
      <div class="table-cell cell-spacer-double" />

    </div>

    <div class="data-wrapper">
    <overlay-scrollbars ref="reportsOverlayScroll" :options="scrollBarOptions">
      
      <TransitionGroup name="fadeRight" tag="div" class="data-container-class">
        
        <div v-for="item in displayItems" :key="item.id" class="table-column" :class="{ 'reloading': item.reloading }">
          <div class="table-header header-year">
            <GekkoButton 
              v-if="item.year"
              @click="recalculate(item.year)"
              :text="$gettext('Recalculate')"
              icon="fa-solid fa-arrows-rotate"
              variant="link"
              size="xs"
              class="p-0"
            />
            <GekkoButton 
              id="set_start_button"
              v-if="!item.year"
              @click="openStartInputs()"
              :text="startButtonText(item)"
              :icon="startButtonIcon(item)"
              variant="link"
              size="xs"
              class="p-0"
            />
            <div>
              <div class="item-year-wrap">
                <div v-if="item.year" class="table-item-year"> {{ item.year }}</div>
                <div v-if="!item.year" class="table-item-year" v-translate> Start</div>
              </div>
              <div class="recalc-options">
                <div class="last-modified-text">
                  <span v-if="item.year === (new Date().getFullYear())">{{ $gettext('On') }} {{ item.modified | formatDate }}</span>
                  <span v-else-if="item.year">{{ $gettext('On') }} {{ $gettext('31 Dec') }} {{ item.year }}</span>
                  <span v-else><div class="recalc-option-spacer" /></span>
                </div>
              </div>
            </div>
          </div>

          <!-- Balance Sheet -->
          
          <div v-show="item.is_start_year && showStartYearInputs">
            <StartBox ref="start_year_inputs" :reportStart="item" @startYearUpdated="onStartYearUpdated" />
          </div>
          <div v-show="!item.is_start_year || !showStartYearInputs">
            <div class="table-cell">
              <h3>€ {{ item.assets | formatNumber }}</h3>
            </div>
            <div class="table-cell">
              € {{ item.balance_bank_account | formatNumber }}
            </div>
            <div class="table-cell">
              <div v-if="item.is_start_year">
                € {{ item.cash | formatNumber }}
              </div>
              <div v-if="!item.is_start_year">
                <div class="table-cell-input" v-if="previousInputField === 'cash' && previousInputYear === item.year" @click="eventStopPropagation($event)">
                  <div class="input-field-div">
                    <b-form-input v-model="item.cash" type="number" class="input-form" />
                  </div>
                  <div class="input-field-div">
                    <GekkoButton 
                      @click="saveInputField(item.id, 'cash', item.cash, item.year)"
                      icon="fa-solid fa-save"
                      variant="link"
                      class="p-0 text-info"
                    />
                  </div>
                </div>

                <div class="table-cell-input" v-if="previousInputField !== 'cash' || previousInputYear !== item.year">
                  <div class="input-field-div">
                    <GekkoButton 
                      @click="enableSaveButton('cash', item.year); eventStopPropagation($event)"
                      icon="fa-solid fa-pen-to-square"
                      variant="link"
                      class="p-0 text-info"
                    />
                  </div>
                  <div class="input-field-div">
                    € {{ item.cash | formatNumber }}
                  </div>
                </div>
              </div>
            </div>
            <div class="table-cell">
              € {{ item.accounts_receivable | formatNumber }}
            </div>
            <div class="table-cell">
              € {{ item.investments | formatNumber }}
              <div class="table-cell cell-spacer-single" />
            </div>
            <div v-if="getLineNamesByType('balance_investments')">
              
              <div v-if="getLineNamesByType('balance_investments').length">
                <div v-for="report_line in getReportLines('balance_investments', item.year)" v-bind:key="report_line.key" class="table-cell dropdown-cell-content">
                  € {{ report_line.value | formatNumber }}
                </div>
              </div>
              <div v-if="!getLineNamesByType('balance_investments').length" class="table-cell cell-spacer-single" />
            </div>
            
            <div class="table-cell cell-spacer-double" />
            <div class="table-cell cell-border-top top-cell-element">
              <h3>€ {{ item.liabilities | formatNumber }}</h3>
            </div>
            <div class="table-cell">
              € {{ item.unpaid_vat | formatNumber }}
            </div>
            <div class="table-cell">
              € {{ item.unpaid_expenses | formatNumber }}
            </div>
            <div class="table-cell">
              <div v-if="item.is_start_year">
                € {{ item.other_debts | formatNumber }}
              </div>
              <div v-if="!item.is_start_year">
                <div class="table-cell-input" v-if="previousInputField === 'other_debts' && previousInputYear === item.year" @click="eventStopPropagation($event)">
                  <div class="input-field-div">
                    <b-form-input v-model="item.other_debts" type="number" class="input-form" />
                  </div>
                  <div class="input-field-div">
                    <GekkoButton 
                      @click="saveInputField(item.id, 'other_debts', item.other_debts, item.year)"
                      icon="fa-solid fa-save"
                      variant="link"
                      class="p-0 text-info"
                    />
                  </div>
                </div>

                <div class="table-cell-input" v-if="previousInputField !== 'other_debts' || previousInputYear !== item.year">
                  <div class="input-field-div">
                    <GekkoButton 
                      @click="enableSaveButton('other_debts', item.year); eventStopPropagation($event)"
                      icon="fa-solid fa-pen-to-square"
                      variant="link"
                      class="p-0 text-info"
                    />
                  </div>
                  <div class="input-field-div">
                    € {{ item.other_debts | formatNumber }}
                  </div>
                </div>
              </div>
            </div>
            <div class="table-cell cell-spacer-single" />
            <div class="table-cell cell-border-top top-cell-element">
              <h3>€ {{ item.equity | formatNumber }}</h3>
            </div>
          </div>

          <div v-show="item.is_start_year">

            <!-- Start year empty cells -->
            <!-- Profit and Loss -->

            <div class="table-cell cell-spacer-double" />
            <div class="table-cell table-header" />
            <div class="table-cell" />
            <div class="table-cell" />
            <div class="table-cell" />
            <div class="table-cell" />
            <div class="table-cell cell-spacer-single" />
            <div class="table-cell cell-border-top top-cell-element" />
            <div class="table-cell">
              <div class="table-cell cell-spacer-single" />
            </div>
            <div class="table-cell cell-spacer-single" />
            <div class="table-cell">
              <div class="table-cell cell-spacer-single" />
            </div>
            <div class="table-cell cell-spacer-single" />
            <div class="table-cell cell-border-top top-cell-element" />
          
            <!-- Withdrawals & deposits -->

            <div class="table-cell cell-spacer-double" />
            <div class="table-cell table-header" />
            <div class="table-cell" />
            <div class="table-cell cell-spacer-double" />
          </div>

          <div v-show="!item.is_start_year">

            <!-- Profit and Loss -->

            <div class="table-cell cell-spacer-double" />
            <div class="table-cell table-header">
              <div v-if="item.year" class="table-item-year year-secondary"> {{ item.year }}</div>
              <div v-if="!item.year" class="table-item-year year-secondary" v-translate> Start</div>
              <div class="last-modified-text">
                <span v-if="item.year === (new Date().getFullYear())">{{ $gettext('On') }} {{ item.modified | formatDate }}</span>
                <span v-else-if="item.year">{{ $gettext('On') }} {{ $gettext('31 Dec') }} {{ item.year }}</span>
                <span v-else>&nbsp;</span>
              </div>
            </div>
            <div class="table-cell">
              <h3>€ {{ item.revenue | formatNumber }}</h3>
            </div>
            <div class="table-cell">
              € {{ item.unpaid_invoices | formatNumber }}
            </div>
            <div class="table-cell">
              € {{ item.paid_invoices | formatNumber }}
            </div>
            <div class="table-cell">
              € {{ item.corrections | formatNumber }}
            </div>
            <div class="table-cell cell-spacer-single" />
            <div class="table-cell cell-border-top top-cell-element">
              <h3>€ {{ item.total_costs | formatNumber }}</h3>
            </div>
            <div class="table-cell">
              € {{ item.expenses | formatNumber }}
              <div class="table-cell cell-spacer-single" />
            </div>
            <div v-if="getLineNamesByType('profit_expenses')">
              
              <div v-if="getLineNamesByType('profit_expenses').length">
                <div v-for="report_line in getReportLines('profit_expenses', item.year)" v-bind:key="report_line.key" class="table-cell dropdown-cell-content">
                  € {{ report_line.value | formatNumber }}
                </div>
              </div>

              <div v-if="!getLineNamesByType('profit_expenses').length" class="table-cell dropdown-cell-content">
                -
              </div>
            </div>
            <div class="table-cell cell-spacer-single" />

            <div class="table-cell">
              € {{ item.write_offs | formatNumber }}
              <div class="table-cell cell-spacer-single" />
            </div>
            <div v-if="getLineNamesByType('profit_writeoffs')">
              
              <div v-if="getLineNamesByType('profit_writeoffs').length">
                <div v-for="report_line in getReportLines('profit_writeoffs', item.year)" v-bind:key="report_line.key" class="table-cell dropdown-cell-content">
                  € {{ report_line.value | formatNumber }}
                </div>
              </div>
              <div v-if="!getLineNamesByType('profit_writeoffs').length" class="table-cell cell-spacer-double" />
            </div>
            <div class="table-cell cell-spacer-single" />
            <div class="table-cell cell-border-top top-cell-element">
              <h3>€ {{ item.profit | formatNumber }}</h3>
            </div>
          
            <!-- Withdrawals & deposits -->

            <div class="table-cell cell-spacer-double" />
            <div class="table-cell table-header">
              <div v-if="item.year" class="table-item-year year-secondary"> {{ item.year }}</div>
              <div v-if="!item.year" class="table-item-year year-secondary" v-translate> Start</div>
              <div class="last-modified-text">
                <span v-if="item.year === (new Date().getFullYear())">{{ $gettext('On') }} {{ item.modified | formatDate }}</span>
                <span v-else-if="item.year">{{ $gettext('On') }} {{ $gettext('31 Dec') }} {{ item.year }}</span>
                <span v-else>&nbsp;</span>
              </div>
            </div>
            <div class="table-cell">
              <h3>€ {{ item.withdrawal | formatNumber }}</h3>
            </div>
            <div class="table-cell cell-spacer-double" />
          </div>


        </div>
        </TransitionGroup>
    </overlay-scrollbars>
    </div>

  </div>

</div>
</template>

<script>
import { mapGetters } from "vuex";

import GekkoButton from "@/components/GekkoButton";
import FinancialReportingService from "@/store/services/financialreporting.service";
import CostTypeService from "@/store/services/costtype.service";
import StartBox from "@/components/balance_sheet/StartBox";

export default {
    name: "ReportTable",
    components: {
      GekkoButton,
      StartBox
    },
    props: {
      details: {
        type: Array,
        default() {
          return []
        }
      }
    },
    computed: {
      ...mapGetters ({
        currentUser: "main/getCurrentUser",
        getCompanyId: 'main/getCompanyId'
      }),
      displayItems () {
        return this.items 
      }
    },
    watch: {
      details(newVal) {
        this.items = newVal;
      }
    },
    data () {
      return {
        items: [],
        costTypeOptions: [],
        showInvestmentsPerCostType: false,
        showExpensesPerCostType: false,
        showWriteOffsPerCostType: false,
        previousInputYear: null,
        previousInputValue: null,
        previousInputField: null,
        currentScroll: null,
        scrollBarOptions: { 
          scrollbars: { 
            visibility       : "auto",
            autoHide         : "never",
            autoHideDelay    : 800,
            dragScrolling    : true,
            clickScrolling   : true,
            touchSupport     : true,
            snapHandle       : true
          }, 
          overflowBehavior: { y: 'scroll', x: 'scroll' }
        },
        showStartYearInputs: false,
        calculating: false
      }
    },
    created () {
      this.items = []
      if (this.details && this.details.length)
        this.items = this.details;
      CostTypeService.list({company: this.getCompanyId}).then((response) => {
        this.costTypeOptions = response.data;
      });

      document.body.addEventListener('click', this.onDocumentClick);
      window.addEventListener('keypress', this.saveInputOnKeyPress);
      this.scrollTableRight();
    },
    mounted () {
      this.attachDraggableListeners()
    },
    destroyed () {
      document.body.removeEventListener('click', this.onDocumentClick);
      window.removeEventListener('keypress', this.saveInputOnKeyPress);
    },
    methods: {
      isLineTypeShown (lineType) {
        if ((lineType === "balance_investments" && this.showInvestmentsPerCostType) 
        || (lineType === "profit_expenses" && this.showExpensesPerCostType) 
        || (lineType === "profit_writeoffs" && this.showWriteOffsPerCostType)) {
          return true;
        }
        return false;
      },
      isReportLineEmpty (lineType, costType) {
        return this.items.map(item => item.report_lines)
        
          .map(report_lines => report_lines
              .find(report_line => {

                  if (!costType) 
                    return report_line.line_type === lineType && !report_line.cost_type;
                  
                  return report_line.line_type === lineType && report_line.cost_type === costType.id;

                }))

          .map(report_line => {
            if (report_line)
              return report_line.value
            else
              return 0;
          })

          .filter(value => value != 0).length === 0;
      },
      getReportLines (lineType, year) {
        let report_lines = [];
        
        let item = this.items.find(item => item.year === year);

        this.costTypeOptions.forEach(cost_type => {
          let report_line = item.report_lines.filter(line =>  line.line_type === lineType).find(line => line.cost_type === cost_type.id);
          let key = lineType + '_' + cost_type.id;
          
          let appendLine = false;
          if (!this.isReportLineEmpty(lineType, cost_type))
            appendLine = true;

          if (appendLine) {
            if (report_line) {
              report_line.key = key;
              report_lines.push(report_line);
            } else {
              report_lines.push({value: 0, key: key});
            }
          }
        });
        
        // append manual inputs
        if (lineType !== 'profit_expenses') {
          let manual_input = item.report_lines.find(line => line.line_type === lineType && !line.cost_type);
          if (manual_input && !this.isReportLineEmpty(lineType, null)) {
            report_lines.push(manual_input);
          }
        }
        
        if (this.isLineTypeShown(lineType))
          return report_lines;
      },
      getLineNamesByType (lineType) {
        let line_names = [];
        this.costTypeOptions.forEach(cost_type => { 
          
          let appendLine = false;
          if (!this.isReportLineEmpty(lineType, cost_type))
            appendLine = true;
          
          if (appendLine)
            line_names.push({name: cost_type.name, key: lineType + '_' + cost_type.id, costType: cost_type});
        });

        // append manual inputs
        if (lineType !== 'profit_expenses') {
          if (!this.isReportLineEmpty(lineType, null))
            line_names.push({name: this.$gettext('Manual Inputs'), key: lineType + '_null'});
        }
        
        if (this.isLineTypeShown(lineType))
          return line_names;
      },
      changeToggleState (lineType) {
        if (!this.showStartYearInputs) {
          if (lineType === "balance_investments") {
            this.showInvestmentsPerCostType = !this.showInvestmentsPerCostType;
          } else if (lineType === "profit_expenses") {
            this.showExpensesPerCostType = !this.showExpensesPerCostType;
          } else if (lineType === "profit_writeoffs") {
            this.showWriteOffsPerCostType = !this.showWriteOffsPerCostType;
          }
        } else {
          this.$store.commit('setAlert', {
              title: this.$gettext("Start years not set"),
              text: this.$gettext("Save the input values of the start year first."),
              variant: "danger",
              duration: 4
          });
        }
      },
      toggleArrowIcon (state) {
          if (state)
            return "fa-solid fa-caret-up";
          else
            return "fa-solid fa-caret-down";
      },
      saveInputField (itemId, field, value, year) {
        let body = {'company_profile': this.getCompanyId, 'year': year}
        body[field] = value;

        FinancialReportingService.update(itemId, body).then((response) => {
          for (var i = 0; this.items.length; i++) {
            if (this.items[i].id === response.data.id) {
              this.items[i] = response.data;
              break;
            }
          }

          this.previousInputYear = null;
          this.previousInputValue = null;
          this.previousInputField = null;
          
          this.$store.commit("setAlert", {
              title: this.$gettext("Report Updated"),
              text: this.$gettext("Field updated correctly."),
              variant: "success",
              duration: 4
          });
        }).catch((response) => {
          let msg = this.$gettext("Something went wrong.");
          if (response.cash)
            msg = response.cash;
          else if (response.other_debts)
            msg = response.other_debts;
          else if (response.balance_bank_account)
            msg = response.balance_bank_account
          this.$store.commit("setAlert", {
              title: this.$gettext("Error Updating Field"),
              text: msg,
              variant: "danger",
              duration: 4
          });
          this.revertInputValues();
        });
      },
      removeReportsFromList (years) {
        years.forEach(year => {
          for (let i = 0; i < this.items.length; i++) {
            if (this.items[i].year === year) {
              this.items.splice(i, 1)
            }
          }
        });
      },
      setColumnsToUpdate (years) {
        years.forEach(year => {
          for (let i = 0; i < this.items.length; i++) {
            if (this.items[i].is_start_year && year === null) {
              this.$set(this.items, i, { ...this.items[i], reloading: false })
            } else if (this.items[i].year === year) {
              this.$set(this.items, i, { ...this.items[i], reloading: true })
            }
          }
        });
      },
      recalculate (year) {
        if (!this.calculating) {
          this.calculating = true

          var isPremium = this.$store.getters['getIsPremium'];
          if (isPremium || this.currentUser.is_accountant) {
            this.currentScroll = this.$refs.reportsOverlayScroll.osInstance().scroll().position.x
            if (window.mixpanel) {
              window.mixpanel.track("Financial Statements: recalculate");
            }

            let years = this.items.map(item => item.year);
            let yearsToRecalculate = years.filter(x => x >= year);

            this.$emit("setProgressBarMax", yearsToRecalculate.length);
            this.$emit("showProgressBar");
          
            // this.removeReportsFromList(yearsToRecalculate);
            this.setColumnsToUpdate(yearsToRecalculate);
            this.recalculateNextYear(yearsToRecalculate);
          } else {
            this.$store.commit('togglePremiumModal', 'web_reports_calculate');
          }
        }
      },
      recalculateNextYear (yearsToRecalculate) {
        if (yearsToRecalculate.length) {
          let year = yearsToRecalculate.shift();
          if (year) {
            FinancialReportingService.recalculate({company_profile: this.getCompanyId, year: year}).then((response) => {
              if (response.status === 204) {
                yearsToRecalculate.unshift(year)
                this.removeReportsFromList(yearsToRecalculate)
                yearsToRecalculate.shift()
              } else {
                this.addReportToTable(response.data)
              }
                this.$emit("increaseProgressBarValue");
                this.recalculateNextYear(yearsToRecalculate);
            }).catch(() => {
              this.$store.commit("setAlert", {
                  title: this.$gettext("Error Updating Field"),
                  text: this.$gettext("Something went wrong."),
                  variant: "danger",
                  duration: 4
              });
              this.loading = false;
            });
          } else { // the start year values should already be updated at onStartYearUpdated()
            this.recalculateNextYear(yearsToRecalculate);
          }
        } else {
          this.calculating = false
          this.$store.commit("setAlert", {
              title: this.$gettext("Reports generated"),
              text: this.$gettext("The reports were generated correctly."),
              variant: "success",
              duration: 4
          });
          this.$emit("endCalculating");
          this.resetScroll();
        }
      },
      resetScroll () {
        this.$refs.reportsOverlayScroll.osInstance().scroll({ x: this.currentScroll }, 1000, 'easeInOutCubic');
      },
      addReportToTable (report) {
          let reportExists = false;
            
          for (var i = 0; i < this.items.length; i++) {
            if (this.items[i].id === report.id) {
              this.$set(this.items, i, report)
              reportExists = true;
              break;
            }
          }

          if (!reportExists) {
            let indexFound = false;
            
            let newItemYear = report.year;
            let years = this.items.map(item => item.year);
            for (var j = 0; j < years.length; j++) {
              if (newItemYear < years[j]) {
                this.items.splice(j, 0, report);
                indexFound = true;
                break;
              }
            }

            if (!indexFound)
              this.items.push(report);
          }

          this.previousInputYear = null;
          this.previousInputValue = null;
          this.previousInputField = null;
      },
      eventStopPropagation (event) {
        if (event)
          event.stopPropagation();
      },
      revertInputValues () {
        if (this.previousInputField && this.previousInputYear && this.previousInputValue) {
          for (var i = 0; i < this.items.length; i++) {
            if (this.items[i].year === this.previousInputYear) {
              if (this.previousInputField === "cash")
                this.items[i].cash = this.previousInputValue;
              else if (this.previousInputField === "other_debts")
                this.items[i].other_debts = this.previousInputValue;
              else if (this.previousInputField === "balance_bank_account")
                this.items[i].balance_bank_account = this.previousInputValue;
            }
          }
        }
      },
      enableSaveButton (field, year) {
        this.revertInputValues();
        this.previousInputField = field;
        this.previousInputYear = year;
        let item = this.items.find(item => item.year === year);
        this.previousInputValue = item[field];
      },
      onDocumentClick () {
        this.revertInputValues();
        this.previousInputYear = null;
        this.previousInputValue = null;
        this.previousInputField = null;
      },
      saveInputOnKeyPress (e) {
        if (e.key === 'Enter') {
          if (this.previousInputField && this.previousInputValue && this.previousInputYear) {
            let item = this.items.find(item => item.year === this.previousInputYear);

            if (this.previousInputField === "balance_bank_account")
              this.saveInputField(item.id, this.previousInputField, item.balance_bank_account, item.year);
            else if (this.previousInputField === "cash")
              this.saveInputField(item.id, this.previousInputField, item.cash, item.year);
            else if (this.previousInputField === "other_debts")
              this.saveInputField(item.id, this.previousInputField, item.other_debts, item.year);
          }
        }
      },
      scrollTableRight () {
        setTimeout(() => {
          this.$refs.reportsOverlayScroll.osInstance().scroll({ x: '100%' }, 1000, 'easeInOutCubic');
        }, 1000);
      },
      isPreviousReportModified (item) {
        let previousItems = this.items.filter(it => it.year < item.year);
        
        for (let i = 0; i < previousItems.length; i++) {
          let it = previousItems[i];
          let prevModified = Date.parse(it.modified);
          let currentModified = Date.parse(item.modified);
          if (prevModified > currentModified)
            return true;
        }
        
        return false;
      },
      isCostTypeBalanceSheet (costType) {
        if (costType) {
          if (costType.is_balance_sheet) return true;
          else return false;
        } else return true;
      },
      attachDraggableListeners () {
        const slider = document.querySelector('.data-container-class')
        let isDown = false
        let startX
        let scrollLeft

        slider.addEventListener('mousedown', (e) => {
          isDown = true
          startX = e.pageX - slider.offsetLeft
          scrollLeft = this.$refs.reportsOverlayScroll.osInstance().scroll().position.x
        })
        slider.addEventListener('mouseleave', () => {
          isDown = false
        })
        slider.addEventListener('mouseup', () => {
          isDown = false
        })
        slider.addEventListener('mousemove', (e) => {
          if(!isDown) return
          e.preventDefault()
          const x = e.pageX - slider.offsetLeft
          const walk = (x - startX)
          this.$refs.reportsOverlayScroll.osInstance().scroll({ x: scrollLeft - walk }, 0, 'linear');
        });
      },
      openStartInputs() {
        if (this.showStartYearInputs) {
          this.$refs.start_year_inputs[0].saveForm();
        } else {
          this.showInvestmentsPerCostType = false;
          this.showExpensesPerCostType = false;
          this.showWriteOffsPerCostType = false;
        }
        this.showStartYearInputs = !this.showStartYearInputs;
      },
      startButtonText(item) {
        if (item.is_start_year && this.showStartYearInputs) {
          return this.$gettext('Save & Recalculate');
        } else {
          return this.$gettext('Set Starting Values');
        }
      },
      startButtonIcon(item) {
        if (item.is_start_year && this.showStartYearInputs) {
          return "fa-solid fa-save";
        } else {
          return "fa-solid fa-play";
        }
      },
      onStartYearUpdated(startYear) {
        let startYearItem = this.items.find((item) => item.is_start_year);
        startYearItem.assets = startYear.assets;
        startYearItem.liabilities = startYear.liabilities;
        startYearItem.equity = startYear.equity;
        this.recalculate(0);
      }
    }
}
</script>

<style lang="scss" scoped>
$mobileLabelColumnSize: 200px;
$desktopLabelColumnSize: 300px;
.report-container {
  display: flex;
  overflow: hidden;
  background: #fafafa;
  border: 1px solid $tableBorderColor;
  border-radius: 12px;
  .data-wrapper {
    width: calc(100% - #{$mobileLabelColumnSize});

    .data-container-class {
      width: 100%;
      display: flex;
      // padding-bottom: 50px;
      cursor: grab;
      .table-column {
        background: white;
        border-right: 1px solid $tableBorderColor;
        transition:  all 0.3s ease-in-out;
        &.reloading {
          opacity: 0.2;
        }
      }
    }
  }
}

.item-year-wrap {
  display: flex;
  align-items: center;
  i {
    margin: 0;
    margin-right: 5px;
    color:#FF8C00;
  }
}
.table-item-year {
  font-weight: 900;
  font-size: 20px;
  width: 100%;
  text-align: right;
  line-height: 0.8;
  margin-bottom: 2px;
}

.dropdown-label, .dropdown-cell-content {
  color: $colorUnassigned;
  font-style: italic;
  &:first-child {
    margin-top: 18px;
  }
}
.dropdown-label {
  font-size: 16px;
}
.dropdown-cell-content {
  background: #f0f0f05c;
}

.input-form {
  max-width: 100px;
  height: 26px;
  padding: 0 5px;
}

.input-button {
  vertical-align: middle;
  color: $colorBlue;
  padding-left: 5px;
  padding-right: 5px;
}

.input-button:hover {
  color: #096288;
  cursor: pointer;
}

.last-modified-text {
  font-size: 12px;
  color: $colorUnassigned;
  text-transform: uppercase;
}

.recalc-option-spacer {
  height: 18px;
}

.empty-text-container {
  text-align: right;
  padding-right: 24px;
  padding-top: 7px;
}

.empty-array-text {
  color: #ff5408;
}

.breakdown-link {
  font-weight: normal;
  font-size: 16px;
  display: block;
  padding: 0;
  border: 0;
  width: 100%;
  text-align: right;
  color: $colorBlue;
}

.report-container {
  .label-column {
    position: relative;
    background: #fafafa;
    box-shadow: 2px 0px 8px 0px rgb(0 0 0 / 10%);
    // padding-bottom: 50px;
    width: $mobileLabelColumnSize;
    z-index: 10;
    > .table-header {
      background-size: 7px 7px;
    }
    > .table-cell {
      min-width: 200px;
      font-weight: 900;
      color: #9b9b9b;
    }
    h3 { 
      font-size: 16px;
      line-height: 24px;
      text-transform: uppercase;
      color: $colorTextBase;
    }
  }
  .data-container-class {
    .table-header {
      background: #fafafa;
      &.header-year {
        position: sticky;
        top: 0;
        z-index: 5;
        display: flex;
        justify-content: space-between;
        padding-top: 7px !important;
      }
    }
  }
}

.table-header, .table-cell {
  padding: 0 0.5rem;
  font-size: 14px;
}

.table-header {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
  white-space: nowrap;
  padding-top: 1rem;
  padding-bottom: 1rem;
  margin-bottom: 1.5rem;
  height: 115px;
  text-align: right;
  color: $colorTextBase !important;
  background: #f6f6f6;
  h1 { 
    padding: 0; margin: 0; 
    font-size: 22px;
  }
}

.table-cell {
  text-align: right;
  white-space: nowrap;
  color: $colorUnassigned;
  min-width: 125px;
  min-height: 2rem;
  max-height: 2rem;

  h3 { 
    white-space: nowrap;
    margin: 0; 
    margin-bottom: 10px;
    padding: 0; 
    color: $colorTextBase;
    line-height: 24px;
    font-size: 22px;
  }
  &.cell-spacer-single, .breakdown-link {
    height: 1rem;
  }
  &.cell-spacer-double {
    height: 2rem;
  }
  &.cell-border-top {
    border-top: 1px solid $tableBorderColor;
    min-height: 3rem;
  }
  &.top-cell-element {
    padding-top: 1rem !important;
  }
  .table-cell-input {
    display: flex;
    justify-content: flex-end;
  }
}


@media(min-width: '576px') {
.report-container {
  .label-column {
    width: $desktopLabelColumnSize;
    > .table-cell {
      min-width: 250px;
    }
  }
  .table-header, .table-cell {
    padding: 0 1.5rem;
    font-size: inherit;
  }
  // .table-cell {
  //   &.cell-border-top {
  //     padding-top: 1.5rem;
  //   }
  // }
  .table-header {
    padding-top: 4rem;
    padding-bottom: 1rem;
  }
  .data-wrapper {
    width: calc(100% - #{$desktopLabelColumnSize});
  }
}

}
</style>

<style lang="scss">
.table-cell {
  .breakdown-link {
    height: 1rem;
  }
}
</style>